import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from '../../../api';
import { DURATION_OPTIONS, VideoProject } from '../types';
import { fieldSx, labelSx, pillButtonSx, vb } from '../theme';
import { apiMessage } from '../useAutosave';
import ElliottWorking from '../../ui/ElliottWorking';

// Prototype guidance screen: what should the video cover + length. Persists
// guidance/duration, then starts the AI draft (§8, §20).
const GuidanceStep: React.FC<{
  project: VideoProject;
  onBack: () => void;
  onDrafted: (project: VideoProject) => void;
}> = ({ project, onBack, onDrafted }) => {
  const [guidance, setGuidance] = useState(project.guidance || '');
  const [seconds, setSeconds] = useState(project.targetDurationSeconds || 90);
  const [busy, setBusy] = useState(false);
  const presenter = project.mode === 'presenter';

  const start = async () => {
    if (busy) return;
    try {
      setBusy(true);
      const patch: any = {};
      if (guidance.trim() !== (project.guidance || '')) patch.guidance = guidance.trim();
      if (seconds !== project.targetDurationSeconds) patch.targetDurationSeconds = seconds;
      if (Object.keys(patch).length) await API.updateVideoProject({ projectId: project.id, patch });
      const res: any = presenter
        ? await API.draftPresenterScript({ projectId: project.id })
        : await API.draftExplainerStoryboard({ projectId: project.id });
      onDrafted(res.data as VideoProject);
    } catch (err) {
      toast.error(
        apiMessage(
          err,
          presenter
            ? 'Elliott could not draft the script. Try again.'
            : 'Elliott could not storyboard the video. Try again.'
        )
      );
      setBusy(false);
    }
  };

  if (busy) {
    return (
      <Box data-testid="drafting">
        <ElliottWorking
          size="full"
          message={
            presenter
              ? 'Elliott is reading the lesson and writing the script...'
              : 'Elliott is turning the lesson into scenes with narration...'
          }
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <Typography sx={{ fontFamily: vb.font, fontSize: 20, fontWeight: 800, color: vb.text }}>
        What should this video cover?
      </Typography>
      <Box>
        <Typography sx={labelSx}>Guidance for Elliott</Typography>
        <TextField
          multiline
          minRows={4}
          fullWidth
          value={guidance}
          onChange={(e) => setGuidance(e.target.value)}
          placeholder="Focus on the three habits that build trust: listening first, understanding the customer's world, and agreeing a clear next step. Keep it concrete with a quick example."
          inputProps={{ 'aria-label': 'Guidance for Elliott', maxLength: 2000, 'data-testid': 'guidance-input' }}
          sx={fieldSx}
        />
        <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary, mt: 0.75 }}>
          The lesson is always the source. Guidance tells Elliott what to emphasize, simplify or prioritize.
        </Typography>
      </Box>
      <Box>
        <Typography sx={labelSx}>Length</Typography>
        <Box
          role="radiogroup"
          aria-label="Video length"
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}
        >
          {DURATION_OPTIONS.map((o) => {
            const selected = o.seconds === seconds;
            return (
              <Box
                key={o.seconds}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                data-testid={`length-${o.seconds}`}
                onClick={() => setSeconds(o.seconds)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSeconds(o.seconds)}
                sx={{
                  p: 1.5,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  border: `2px solid ${selected ? vb.brand : vb.border}`,
                  background: selected ? 'rgba(247,148,32,0.06)' : vb.page
                }}
              >
                <Typography sx={{ fontFamily: vb.font, fontSize: 15, fontWeight: 800, color: vb.text }}>
                  {o.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: vb.font,
                    fontSize: 12,
                    color: o.hint === 'Recommended' ? vb.brand : vb.textTertiary
                  }}
                >
                  {o.hint}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button onClick={onBack} sx={pillButtonSx()}>
          Previous
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button data-testid="guidance-start" onClick={start} sx={pillButtonSx(true)}>
          {presenter ? 'Draft Script' : 'Storyboard Video'}
        </Button>
      </Box>
      <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary, textAlign: 'right' }}>
        {presenter
          ? 'You review and edit every line before anything is recorded.'
          : 'You can edit, reorder and regenerate scenes before the preview.'}
      </Typography>
    </Box>
  );
};

export default GuidanceStep;
